import { useState, useEffect, useCallback } from 'react';
import { ActivityConfig } from '../types';
import { apiService } from '../services/apiService';

export const useActivities = () => {
  const [activities, setActivities] = useState<ActivityConfig[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadActivities = useCallback(async () => {
    try {
      setLoading(true);
      const activityData = await apiService.getActivityConfigs();
      setActivities(activityData);
      setError(null);
    } catch (err) {
      setError('Failed to load activities');
      console.error('Error loading activities:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const createActivity = async (activityData: Omit<ActivityConfig, 'id'>) => {
    try {
      const newActivity = await apiService.createActivityConfig(activityData);
      await loadActivities(); // Refresh activities
      return newActivity;
    } catch (err) {
      setError('Failed to create activity');
      throw err;
    }
  };

  const updateActivity = async (activityId: string, activityData: Partial<ActivityConfig>) => {
    try {
      await apiService.updateActivityConfig(activityId, activityData);
      await loadActivities(); // Refresh activities
    } catch (err) {
      setError('Failed to update activity');
      throw err;
    }
  };

  const deleteActivity = async (activityId: string) => {
    try {
      await apiService.deleteActivityConfig(activityId);
      await loadActivities(); // Refresh activities
    } catch (err) {
      setError('Failed to delete activity');
      throw err;
    }
  };

  useEffect(() => {
    loadActivities();
  }, [loadActivities]);

  return {
    activities,
    loading,
    error,
    loadActivities,
    createActivity,
    updateActivity,
    deleteActivity
  };
};